/*
  Internet-only network quality monitor.

  - Uses browser connectivity signals only (no backend timing/error samples).
  - Evaluates quality on timers (3 min light, 15 min expanded).
  - Uses Network Information API when available (navigator.connection).
*/

// Keep thresholds pragmatic and conservative to avoid UI noise.
const THRESHOLDS = {
  degraded: {
    rttMs: 600,
    downlinkMbps: 1.5,
    effectiveTypes: ['3g']
  },
  poor: {
    rttMs: 1200,
    downlinkMbps: 0.8,
    effectiveTypes: ['2g', 'slow-2g']
  }
};

const safeToNumber = value => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getConnectionSnapshot = () => {
  if (typeof navigator === 'undefined') return null;
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  if (!connection) return null;
  return {
    effectiveType: connection.effectiveType || null,
    downlinkMbps: safeToNumber(connection.downlink),
    rttMs: safeToNumber(connection.rtt),
    saveData: Boolean(connection.saveData)
  };
};

const getOnlineSnapshot = () => {
  if (typeof navigator === 'undefined') return null;
  return navigator.onLine !== false;
};

const classifyQuality = ({ isOnline, connection }) => {
  if (isOnline === false) return 'offline';

  const effectiveType = connection?.effectiveType || null;
  const downlinkMbps = connection?.downlinkMbps;
  const rttMs = connection?.rttMs;

  const poorByConnection =
    (effectiveType && THRESHOLDS.poor.effectiveTypes.includes(effectiveType)) ||
    (typeof downlinkMbps === 'number' &&
      downlinkMbps > 0 &&
      downlinkMbps < THRESHOLDS.poor.downlinkMbps) ||
    (typeof rttMs === 'number' && rttMs > THRESHOLDS.poor.rttMs);

  const degradedByConnection =
    (effectiveType &&
      THRESHOLDS.degraded.effectiveTypes.includes(effectiveType)) ||
    (typeof downlinkMbps === 'number' &&
      downlinkMbps > 0 &&
      downlinkMbps < THRESHOLDS.degraded.downlinkMbps) ||
    (typeof rttMs === 'number' && rttMs > THRESHOLDS.degraded.rttMs);

  if (poorByConnection) return 'poor';
  if (degradedByConnection) return 'degraded';
  if (isOnline === true) return 'healthy';
  if (connection) return 'healthy';
  return 'unknown';
};

export const evaluateNetworkQuality = ({ windowMs, includeConnection }) => {
  const isOnline = getOnlineSnapshot();
  const connection = includeConnection ? getConnectionSnapshot() : null;
  const quality = classifyQuality({ isOnline, connection });

  return {
    quality,
    stats: {
      windowMs,
      isOnline,
      connection
    }
  };
};
